// The toolbar owns the one breadcrumb trail. It is derived from the URL alone, so every route shows the same
// project › section › object › leaf shape and pages do not repeat their own crumbs above the title.
export type Breadcrumb = { label: string; to?: string; emphasis?: boolean };

const SECTION_LABELS: Readonly<Record<string, [label: string, path: string]>> = {
  datasets: ["Datasets", "datasets"], views: ["Views", "views"],
  subsets: ["Subsets", "subsets"], "training-records": ["Training record", "training-records"],
  uploads: ["Uploads", "uploads"], "upload-sessions": ["Uploads", "uploads"], "merge-plans": ["Merge plans", "merge-plans"],
  "label-exports": ["Merge plans", "merge-plans"], reviews: ["Reviews", "reviews"], receipts: ["Receipts", "receipts"],
  lineage: ["Lineage", "lineage"], workflows: ["Workflows", "workflows"], models: ["Models", "models"],
  inference: ["Inference & Test", "inference"],
  train: ["Train a model", "train"], "model-comparison": ["Models", "models"], "model-evaluations": ["Models", "models"],
  "model-return": ["Models", "models"], "model-factory": ["Models", "models"], "base-models": ["Models", "models"],
  jobs: ["Jobs & Runs", "jobs"], audit: ["Audit log", "audit"], deletions: ["Deletion requests", "deletions"],
  settings: ["Project settings", "settings"],
};

const VERSION_TABS: Readonly<Record<string, string>> = {
  stats: "Statistics", taxonomy: "Classes", leakage: "Leakage checks", samples: "Samples", labelling: "Labeling rounds",
  labeling: "Labeling", views: "Views and models",
};

const decode = (part: string) => { try { return decodeURIComponent(part); } catch { return part; } };
const short = (value: string) => (/^[0-9a-f-]{16,}$/i.test(value) ? value.slice(0, 8) : value);
const versionLabel = (ref: string) => (/^\d+$/.test(ref) ? `v${ref}` : short(ref));

/** The trail after the project crumb for `/p/:slug/...`, ending with the emphasized current page. */
export function projectTrail(pathname: string, search: string, projectLink: string): Breadcrumb[] {
  const parts = pathname.split("/").filter(Boolean).slice(2).map(decode);
  const [first, second, third, fourth, fifth] = parts;
  if (!first) return [{ label: "Overview", emphasis: true }];
  const params = new URLSearchParams(search);
  const known = Object.prototype.hasOwnProperty.call(SECTION_LABELS, first) ? SECTION_LABELS[first] : undefined;
  if (!known) return [{ label: decode(first), emphasis: true }];
  const [label, path] = known;
  const sectionLink = `${projectLink}/${path}`;
  const trail: Breadcrumb[] = [];
  const leaf = (text: string) => trail.push({ label: text, emphasis: true });
  const link = (text: string, to: string) => trail.push({ label: text, to });

  // Project administration pages live under Project settings.
  if (first === "audit" || first === "deletions") {
    link("Project settings", `${projectLink}/settings`);
    leaf(label);
    return trail;
  }
  if (first === "model-return") { link(label, sectionLink); leaf("Upload a trained model"); return trail; }
  if (first === "model-comparison") { link(label, sectionLink); leaf("Compare models"); return trail; }
  if (first === "model-evaluations") { link(label, sectionLink); if (third === "analysis") { link("Evaluation", `${projectLink}/model-evaluations/${second}`); leaf("Analysis"); } else leaf("Evaluation"); return trail; }
  if (first === "training-records") { link("Training records", `${projectLink}/lineage?view=record`); leaf(third ? short(third) : "Record"); return trail; }
  if (first === "upload-sessions") { link(label, sectionLink); leaf("Upload preparation"); return trail; }
  if (first === "label-exports" && second) { link(label, sectionLink); leaf(`Label export ${second}`); return trail; }
  if (first === "lineage") {
    if (second === "receipts" && third) { link(label, sectionLink); leaf(`Contribution ${third}`); return trail; }
    if (second === "graph" || params.get("view") === "matrix") { link(label, sectionLink); leaf(params.get("view") === "matrix" ? "Data–model matrix" : "Graph"); return trail; }
    leaf(label);
    return trail;
  }
  if (!second) {
    const source = params.get("source");
    if (first === "models" && (source === "shared" || source === "official")) {
      link(label, sectionLink); leaf(source === "shared" ? "Shared" : "Official");
    } else leaf(label);
    return trail;
  }
  link(label, sectionLink);

  switch (first) {
    case "datasets": {
      const dataset = `${sectionLink}/${encodeURIComponent(second)}`;
      if (!third) { leaf(second); break; }
      link(second, dataset);
      if (third === "compare") leaf("Compare versions");
      else if (third === "drafts" && fourth) leaf(`Draft D${fourth}`);
      else if (third === "subsets") leaf("New subset");
      else if (third === "v" && fourth) {
        const version = versionLabel(fourth);
        if (fifth === "samples" && parts[5]) { link(version, `${dataset}/v/${encodeURIComponent(fourth)}`); leaf(`Sample ${short(parts[5])}`); }
        else if (fifth === "analysis") { link(version, `${dataset}/v/${encodeURIComponent(fourth)}?tab=quality`); leaf("Analysis"); }
        else {
          const tab = params.get("tab");
          if (tab && VERSION_TABS[tab]) { link(version, `${dataset}/v/${encodeURIComponent(fourth)}`); leaf(VERSION_TABS[tab]); }
          else leaf(version);
        }
      } else leaf(third);
      break;
    }
    case "models": {
      if (second === "submissions") { leaf("Submission"); break; }
      if (second === "registrations") { leaf("Registration"); break; }
      const line = `${sectionLink}/${encodeURIComponent(second)}`;
      if (!third) { leaf(second); break; }
      link(second, line);
      leaf(third === "compare" ? "Compare" : third === "train" ? "Train a model" : versionLabel(third));
      break;
    }
    case "merge-plans":
      if (second === "new") leaf("New merge plan");
      else if (third === "conflicts") { link(`M${second}`, `${sectionLink}/${encodeURIComponent(second)}`); leaf("Conflicts"); }
      else leaf(`M${second}`);
      break;
    case "views":
      if (second === "new") leaf("Build a view");
      else if (third === "analysis") { link(second, `${sectionLink}/${encodeURIComponent(second)}`); leaf("Analysis"); }
      else leaf(second);
      break;
    case "uploads":
      leaf(second === "new" ? "New upload" : short(second));
      break;
    case "receipts":
      leaf(`Receipt P${second}`);
      break;
    case "reviews":
      if (second === "drafts" && third) leaf(`${third} D${fourth ?? ""}${fifth ? ` r${fifth}` : ""}`);
      else leaf(second);
      break;
    default:
      leaf(short(second));
  }
  return trail;
}
